import React, { useMemo, useState } from 'react';

import {
  type PatientImportEditableField,
  type PatientImportResolveKind,
  type PatientImportRowDedupeStatus,
} from '@eluve/api-contract';
import {
  Button,
  Caption,
  Icon,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Stack,
  Text,
  cn,
  tv,
} from '@eluve/components';

import { FIELDS, FieldEditor } from '../components/RowCard';
import { WhiteCard } from '../components/WhiteCard';
import { type useImportRowMutations } from '../hooks/useImportRowMutations';
import { displayValueForField } from '../utils';

import {
  CATEGORY_CONFIG,
  type Counts,
  type RowWithIssues,
  type Severity,
} from './review-grouping';

type StatusFilter = 'all' | 'errors' | 'ready';

const isRowReady = (row: RowWithIssues): boolean => {
  if (row.resolution !== null) return true;
  if (row.issues.length > 0) return false;
  return (
    row.dedupeStatus === 'NEW' || row.dedupeStatus === 'DUPE_ID_EXACT_MATCH'
  );
};

const chipCountStyles = tv({
  base: 'ml-1 rounded-full px-1.5 py-0.5 text-2xs font-semibold',
  variants: {
    tone: {
      err: 'bg-bg-negative-subtle text-text-negative',
      ok: 'bg-bg-positive-subtle text-text-positive-subtle',
      neutral: 'bg-bg-secondary text-text-tertiary',
    },
  },
  defaultVariants: { tone: 'neutral' },
});

const FilterChip: React.FC<{
  label: string;
  count: number;
  active: boolean;
  tone?: 'err' | 'ok';
  onClick: () => void;
}> = ({ label, count, active, tone, onClick }) => {
  return (
    <Button size="sm" variant={active ? 'outline' : 'ghost'} onClick={onClick}>
      {label}
      <span className={chipCountStyles({ tone })}>{count}</span>
    </Button>
  );
};

const RowActions: React.FC<{
  row: RowWithIssues;
  onResolve: (kind: PatientImportResolveKind) => void;
  onUndo: () => void;
}> = ({ row, onResolve, onUndo }) => {
  if (row.resolution !== null) {
    return (
      <Button size="xs" variant="ghost" onClick={onUndo}>
        <Icon name="RefreshCw" size="auto" />
        Undo
      </Button>
    );
  }
  return (
    <Stack direction="horizontal" gap={1} justify="end" fullWidth={false}>
      <Button size="xs" variant="ghost" onClick={() => onResolve('SKIPPED')}>
        <Icon name="X" size="auto" />
        Skip
      </Button>
      <Button
        size="xs"
        variant="ghost"
        onClick={() => onResolve('REMOVED')}
        className="text-text-negative"
      >
        <Icon name="Trash" size="auto" />
        Delete
      </Button>
    </Stack>
  );
};

export const PatientImportFullFileView: React.FC<{
  rows: RowWithIssues[];
  counts: Counts;
  updateField: ReturnType<typeof useImportRowMutations>['updateField'];
  resolveRow: ReturnType<typeof useImportRowMutations>['resolveRow'];
  unresolveRow: ReturnType<typeof useImportRowMutations>['unresolveRow'];
}> = ({ rows, counts, updateField, resolveRow, unresolveRow }) => {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((row) => {
      const ready = isRowReady(row);
      if (statusFilter === 'errors' && ready) return false;
      if (statusFilter === 'ready' && !ready) return false;
      if (q) {
        const hay = [
          row.patientId,
          row.firstName,
          row.lastName,
          row.email,
          row.phoneNumber,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, statusFilter, query]);

  const errorCount = counts.blocking;

  return (
    <WhiteCard className="overflow-hidden p-0">
      <Stack
        direction="horizontal"
        justify="between"
        align="center"
        className="border-b border-border-tertiary bg-bg-secondary px-3 py-2"
      >
        <Stack direction="horizontal" gap={2} align="center">
          <InputGroup className="w-72">
            <InputGroupAddon align="inline-start">
              <Icon name="Search" size="xs" />
            </InputGroupAddon>
            <InputGroupInput
              placeholder="Search patients"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </InputGroup>
          <Stack direction="horizontal" gap={1} align="center">
            <FilterChip
              label="All"
              count={counts.total}
              active={statusFilter === 'all'}
              onClick={() => setStatusFilter('all')}
            />
            <FilterChip
              label="Errors"
              count={errorCount}
              tone="err"
              active={statusFilter === 'errors'}
              onClick={() => setStatusFilter('errors')}
            />
            <FilterChip
              label="Ready"
              count={counts.ready}
              tone="ok"
              active={statusFilter === 'ready'}
              onClick={() => setStatusFilter('ready')}
            />
          </Stack>
        </Stack>
        <Text size="sm" color="tertiary">
          Showing {filtered.length} of {counts.total}
        </Text>
      </Stack>

      <div className="max-h-[780px] overflow-auto">
        <table className="w-full border-separate border-spacing-0 text-sm">
          <thead className="sticky top-0 bg-bg-secondary">
            <tr className="[&>th]:border-b [&>th]:border-border-tertiary">
              <th className="w-10 px-3 py-2 text-right">
                <Caption color="tertiary" size="2xs">
                  #
                </Caption>
              </th>
              {FIELDS.map(({ field }) => (
                <th key={field} className="px-3 py-2 text-left">
                  <Caption
                    color="tertiary"
                    size="2xs"
                    className="font-mono normal-case"
                  >
                    {field}
                  </Caption>
                </th>
              ))}
              <th className="w-32 px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => (
              <FullFileRow
                key={row.id}
                row={row}
                onUpdate={(field, value) => updateField(row.id, field, value)}
                onResolve={(kind) => resolveRow(row.id, kind)}
                onUndo={() => unresolveRow(row.id)}
              />
            ))}
          </tbody>
        </table>
      </div>

      <Stack
        direction="horizontal"
        justify="between"
        align="center"
        className="border-t border-border-tertiary bg-bg-secondary px-3 py-2"
      >
        <Text size="xs" color="tertiary">
          {counts.total} rows · {counts.blocking} errors ·{' '}
          {counts.informational} warnings · {counts.ready} ready
        </Text>
      </Stack>
    </WhiteCard>
  );
};

const DEDUPE_SEVERITY: Partial<Record<PatientImportRowDedupeStatus, Severity>> =
  {
    DUPE_IN_FILE: 'error',
    DUPE_ID_DIFFERENT_PATIENT: 'error',
    DUPE_ID_SAME_PATIENT: 'error',
    PATIENT_EXISTS_NO_ID_MATCH: 'warn',
    DUPE_ID_EXACT_MATCH: 'info',
  };

const rowSeverity = (row: RowWithIssues): Severity | null => {
  const issueCategory = row.issues[0]?.category;
  if (issueCategory) {
    return CATEGORY_CONFIG[issueCategory]?.severity ?? null;
  }
  return DEDUPE_SEVERITY[row.dedupeStatus] ?? null;
};

type RowToneState = 'skipped' | 'resolved' | Severity | 'default';

const rowToneStyles = tv({
  slots: {
    cell: 'border-b border-border-tertiary',
    rail: 'border-l-4',
  },
  variants: {
    state: {
      skipped: {
        cell: 'bg-bg-secondary',
        rail: 'border-l-border-tertiary',
      },
      resolved: {
        cell: 'bg-bg-positive-subtle',
        rail: 'border-l-bg-positive-strong-primary',
      },
      error: {
        cell: 'bg-bg-negative-subtle',
        rail: 'border-l-bg-negative-strong-primary',
      },
      warn: {
        cell: 'bg-bg-warning-subtle',
        rail: 'border-l-bg-warning-strong-primary',
      },
      info: { cell: '', rail: 'border-l-bg-info-strong-primary' },
      default: { cell: '', rail: 'border-l-transparent' },
    },
  },
  defaultVariants: { state: 'default' },
});

const rowToneState = (args: {
  skipped: boolean;
  resolved: boolean;
  severity: Severity | null;
}): RowToneState => {
  if (args.skipped) return 'skipped';
  if (args.resolved) return 'resolved';
  return args.severity ?? 'default';
};

const FullFileRow: React.FC<{
  row: RowWithIssues;
  onUpdate: (
    field: PatientImportEditableField,
    value: string | null,
  ) => Promise<unknown>;
  onResolve: (kind: PatientImportResolveKind) => void;
  onUndo: () => void;
}> = ({ row, onUpdate, onResolve, onUndo }) => {
  const blockingByField = new Map<string, string>();
  for (const issue of row.issues) {
    if (issue.blocking && issue.field && !blockingByField.has(issue.field)) {
      blockingByField.set(issue.field, issue.message);
    }
  }
  const skipped = row.resolution === 'SKIPPED' || row.resolution === 'REMOVED';
  const resolved = row.resolution !== null;
  const severity = rowSeverity(row);
  const tone = rowToneStyles({
    state: rowToneState({ skipped, resolved, severity }),
  });
  const muted = skipped ? 'line-through opacity-60' : '';

  return (
    <tr className={cn('align-top', muted)}>
      <td className={cn('px-3 py-2 text-right', tone.cell(), tone.rail())}>
        <Caption color="tertiary" size="2xs" className="font-mono">
          {row.rowNumber}
        </Caption>
      </td>
      {FIELDS.map(({ key, field, label }) => (
        <td key={field} className={cn('p-2', tone.cell())}>
          <FieldEditor
            value={displayValueForField(
              field,
              (row[key] as string | null) ?? null,
            )}
            blocking={blockingByField.has(field)}
            errorMessage={blockingByField.get(field)}
            placeholder={label}
            onSave={async (next) => {
              await onUpdate(field, next);
            }}
          />
        </td>
      ))}
      <td className={cn('px-3 py-2 align-middle', tone.cell())}>
        <RowActions row={row} onResolve={onResolve} onUndo={onUndo} />
      </td>
    </tr>
  );
};
